import React from 'react';
import ProductCard from './ProductCard';
import ProductNewRibbon from './ProductNewRibbon';
import PriceWithVatLabel from './PriceWithVatLabel';
import { Product } from '../types';
import {
  MIKROFON_TIER_STICKY_NAV,
  groupMikrofonProducts,
  type MikrofonTierId,
} from '../config/mikrofonProductTiers';
import { trackBookingCtaClick } from '../utils/siteAnalytics';

type ViewMode = 'grid' | 'list';

type Props = {
  products: Product[];
  viewMode: ViewMode;
};

function tierSectionClass(tier: MikrofonTierId, primary: boolean): string {
  if (!primary) {
    return 'border-slate-800/70 bg-slate-900/20';
  }
  switch (tier) {
    case 'tradlosa':
      return 'border-purple-500/35 bg-gradient-to-br from-purple-950/35 via-slate-900/35 to-slate-900/20';
    case 'sang_instrument':
      return 'border-slate-700 bg-slate-900/35';
    case 'headset_presentation':
      return 'border-slate-600/70 bg-slate-950/45';
    default:
      return 'border-slate-800 bg-slate-900/30';
  }
}

function tierTitleClass(primary: boolean, tier: MikrofonTierId): string {
  if (!primary) return 'text-slate-300 text-xl md:text-2xl';
  if (tier === 'tradlosa') return 'text-purple-200 text-3xl md:text-4xl';
  return 'text-white text-3xl md:text-4xl';
}

function tierAccentBar(tier: MikrofonTierId, primary: boolean): string {
  if (!primary) return 'bg-slate-700';
  switch (tier) {
    case 'tradlosa':
      return 'bg-purple-500';
    case 'sang_instrument':
      return 'bg-slate-500';
    case 'headset_presentation':
      return 'bg-slate-400';
    default:
      return 'bg-slate-600';
  }
}

const ProductListRow: React.FC<{ product: Product }> = ({ product }) => (
  <div className="bg-slate-900/35 border border-slate-800 overflow-hidden transition-colors hover:border-slate-700">
    <div className="flex flex-row">
      <div className="relative w-32 h-24 bg-slate-900/20 border-r border-slate-700 flex-shrink-0 overflow-hidden">
        <ProductNewRibbon newUntil={product.newUntil} size="sm" />
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain"
          loading="lazy"
        />
      </div>

      <div className="flex-1 p-4">
        <div className="flex flex-row justify-between items-start">
          <div className="flex-1 mr-4">
            <div className="flex items-center mb-2">
              <span className="inline-flex items-center bg-slate-800 border border-slate-700 text-slate-200 text-xs font-medium px-2 py-1 rounded-none mr-2">
                {product.category}
              </span>
              {product.popular && (
                <span className="inline-flex items-center bg-purple-600 text-white text-xs font-medium px-2 py-1 rounded-none">
                  Populär
                </span>
              )}
            </div>

            <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{product.name}</h3>
            <p className="text-slate-400 text-sm line-clamp-2 leading-relaxed mb-3">
              {product.shortDescription}
            </p>

            <div className="flex items-center justify-between">
              <div>
                {product.priceOnRequest ? (
                  <>
                    <div className="text-xl font-bold text-purple-300">Enligt offert</div>
                    <div className="text-xs text-slate-400">Begär offert</div>
                  </>
                ) : (
                  <>
                    <PriceWithVatLabel
                      price={product.pricePerDay}
                      valueClassName="text-xl font-bold text-white"
                      labelClassName="text-xs text-slate-400"
                    />
                    <div className="text-xs text-slate-400">per dag</div>
                  </>
                )}
              </div>

              <div className="flex flex-row gap-2">
                <a
                  href={`/booking/${product.id}`}
                  onClick={() =>
                    trackBookingCtaClick(product, undefined, {
                      entry_point: 'grid',
                      list_name: 'MikrofonTieredProductGrid',
                    })
                  }
                  className="inline-flex justify-center items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-none transition-colors text-sm"
                >
                  Boka nu
                </a>
                <a
                  href={`/produkter/${product.slug || product.id}`}
                  className="inline-flex justify-center items-center bg-slate-900/20 hover:bg-slate-900/30 text-white font-medium py-2 px-4 rounded-none transition-colors duration-300 border border-slate-700 text-sm"
                >
                  Detaljer
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MikrofonTieredProductGrid: React.FC<Props> = ({ products, viewMode }) => {
  const groups = groupMikrofonProducts(products);
  void MIKROFON_TIER_STICKY_NAV;

  if (groups.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-lg text-slate-400">
          Inga produkter hittades. Försök med en annan sökning eller kategori.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-12" data-testid="mikrofon-tiered-product-grid">
      {groups.map((group) => (
        <section
          key={group.tier}
          id={`mikrofon-tier-${group.tier}`}
          className={`relative border rounded-none ${
            group.primary ? 'p-5 md:p-8' : 'p-4 md:p-5 mt-4'
          } ${tierSectionClass(group.tier, group.primary)}`}
          aria-labelledby={`mikrofon-tier-heading-${group.tier}`}
        >
          <div
            className={`absolute left-0 top-0 bottom-0 ${
              group.primary ? 'w-1' : 'w-0.5'
            } ${tierAccentBar(group.tier, group.primary)}`}
          />

          <header className={`mb-5 md:mb-7 pl-3 md:pl-4 ${group.primary ? '' : 'mb-4'}`}>
            {group.primary ? (
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                Mikrofon · {group.title}
              </p>
            ) : (
              <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">
                Övrigt i kategorin
              </p>
            )}
            <h2
              id={`mikrofon-tier-heading-${group.tier}`}
              className={`font-bold tracking-tight ${tierTitleClass(group.primary, group.tier)}`}
            >
              {group.title}
            </h2>
            <p
              className={`mt-2 max-w-2xl leading-relaxed ${
                group.primary
                  ? 'text-base md:text-lg text-slate-300'
                  : 'text-sm text-slate-400'
              }`}
            >
              {group.blurb}
            </p>
          </header>

          <div className="md:hidden grid grid-cols-1 gap-4">
            {group.products.map((product) => (
              <ProductCard key={`${group.tier}-${product.id}`} product={product} />
            ))}
          </div>

          {viewMode === 'grid' ? (
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {group.products.map((product) => (
                <ProductCard key={`${group.tier}-${product.id}`} product={product} />
              ))}
            </div>
          ) : (
            <div className="hidden md:block space-y-4">
              {group.products.map((product) => (
                <ProductListRow key={`${group.tier}-${product.id}`} product={product} />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
};

export default MikrofonTieredProductGrid;
